import { api } from './api';
import { ListQuery, ManyEnvelope, OneEnvelope, listQueryToParams } from '../types/api';

/**
 * Generic CRUD service bound to a backend resource path. Used by
 * `useCrudResource` and module-level pages to avoid one-off axios calls.
 *
 * Mirrors the response shapes emitted by `BaseController` in the backend.
 */
export class CrudService<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  constructor(private readonly path: string) {}

  async list(query?: ListQuery): Promise<ManyEnvelope<T>> {
    const res = await api.get<ManyEnvelope<T>>(this.path, { params: listQueryToParams(query) });
    return res.data;
  }

  async get(id: number | string, include?: string[]): Promise<T> {
    const res = await api.get<OneEnvelope<T>>(`${this.path}/${id}`, {
      params: include?.length ? { include: include.join(',') } : undefined,
    });
    return res.data.data;
  }

  async create(payload: TCreate): Promise<T> {
    const res = await api.post<OneEnvelope<T>>(this.path, payload);
    return res.data.data;
  }

  async update(id: number | string, payload: TUpdate): Promise<T> {
    const res = await api.put<OneEnvelope<T>>(`${this.path}/${id}`, payload);
    return res.data.data;
  }

  async remove(id: number | string): Promise<void> {
    await api.delete(`${this.path}/${id}`);
  }
}

/**
 * Junction-specific service for `/parents/:parentId/related/:relatedId`.
 * `attach` PUTs by the pair (idempotent on the backend); `detach` DELETEs.
 */
export class JunctionService<T, TBody = Record<string, never>> {
  constructor(private readonly path: string) {}

  async list(query?: ListQuery): Promise<ManyEnvelope<T>> {
    const res = await api.get<ManyEnvelope<T>>(this.path, { params: listQueryToParams(query) });
    return res.data;
  }

  async attach(relatedId: number, body: TBody = {} as TBody): Promise<T> {
    const res = await api.put<OneEnvelope<T>>(`${this.path}/${relatedId}`, body);
    return res.data.data;
  }

  async detach(relatedId: number): Promise<void> {
    await api.delete(`${this.path}/${relatedId}`);
  }
}
